import { useState } from "react";
import { Alert, Button, TextField } from "@mui/material";
import dayjs from "dayjs";
import Badge from "../../components/ui/badge/Badge";
import { useStateValue } from "../../context/StateValueContext";
import type { ItemState, OnboardingAnswers, OnboardingDetailItem, OnboardingHistoryEntry } from "../../types/onboarding";
import type { StepEdit } from "./useOnboarding";
import { ITEM_NETWORK_LABELS, partyLabel } from "./labels";
import ItemDetails from "./ItemDetails";
import { STATUS_CLASSES, toHistoryText, toSegments, toTexts, type Row } from "./checklistRows";

interface Props {
  row: Row;
  answers: OnboardingAnswers;
  items: OnboardingDetailItem[];
  createdAt: string;
  history: OnboardingHistoryEntry[];
  onChange: (items: OnboardingDetailItem[], edit: StepEdit) => Promise<string | null>;
  saving: boolean;
  readOnly: boolean;
}

const PRESSED_CLASSES: Record<ItemState, string> = {
  todo: "bg-gray-800 text-white dark:bg-white/90 dark:text-gray-900",
  waiting: "bg-warning-500 text-white",
  done: "bg-success-600 text-white",
  not_needed: "bg-gray-400 text-white dark:bg-gray-600",
};

export default function StepPanel({ row, answers, items, createdAt, history, onChange, saving, readOnly }: Props) {
  const [{ user }] = useStateValue();
  const [copied, setCopied] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const many = row.items.length > 1;
  const itemsById = new Map(row.items.map((i) => [i.itemId, i]));
  const rowHistory = history.flatMap((entry) => {
    const item = itemsById.get(entry.itemId);
    return item ? [{ entry, item }] : [];
  });
  // A step an edit or staff added has that in its history; the others came with the onboarding.
  const createdWithOnboarding = row.items.some((i) => !rowHistory.some(({ entry }) => entry.itemId === i.itemId && entry.action === "added"));

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
    } catch {
      // navigator.clipboard rejects in insecure contexts; nothing to recover here
    }
  };
  const save = async (item: OnboardingDetailItem, body: StepEdit) => {
    const failed = await onChange([item], body);
    setError(failed);
    return failed;
  };

  return (
    <>
      <div className="flex flex-col gap-2.5 border-b border-gray-100 p-5 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <Badge size="sm" color="light">{partyLabel(row.items[0])}</Badge>
          {many && <span className={`text-sm font-medium ${row.overdue ? "text-error-600" : STATUS_CLASSES[row.status]}`}>{row.label}</span>}
        </div>
        <h2 className="text-lg font-semibold leading-snug text-gray-800 dark:text-white/90">{row.title}</h2>
        {row.items.map((item) => (
          <StatusSwitch
            key={item.itemId} item={item} brand={many ? item.brandName : null} history={history} saving={saving} readOnly={readOnly}
            onMove={(state) => save(item, { state })}
          />
        ))}
        {error && <Alert severity="error">{error}</Alert>}
      </div>
      <div className="flex flex-col gap-5 p-5">
        {toTexts(row).filter((t) => t.text).map((t) => (
          <div key={t.key} className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-gray-500">What needs to happen{t.brand ? ` · ${t.brand}` : ""}</span>
              <Button size="small" onClick={() => copy(t.key, t.text)}>{copied === t.key ? "Copied" : "Copy"}</Button>
            </div>
            <div className="whitespace-pre-line rounded-lg bg-gray-50 p-3 text-sm leading-relaxed text-gray-700 dark:bg-white/5 dark:text-gray-300">
              {t.text}
            </div>
          </div>
        ))}
        {!many && (
          <ItemDetails
            key={row.items[0].itemId}
            item={row.items[0]} answers={answers} items={items} saving={saving}
            onSave={(details) => save(row.items[0], { details })}
          />
        )}
        {row.items.map((item) => (
          <NotesField
            key={item.itemId}
            label={many ? `Notes · ${item.brandName}` : "Notes"}
            notes={item.notes ?? ""}
            saving={saving}
            onSave={(notes) => save(item, { notes })}
          />
        ))}
        <div className="flex flex-col gap-1.5">
          <span className="text-xs font-medium text-gray-500">History</span>
          <ul aria-label="History" className="flex flex-col gap-1 text-sm text-gray-600 dark:text-gray-400">
            {rowHistory.map(({ entry, item }) => <li key={entry.historyId}>{toHistoryText(entry, item, many, user?.email)}</li>)}
            {createdWithOnboarding && <li className="text-gray-400">{dayjs.utc(createdAt).format("MMM D")} · Created</li>}
          </ul>
        </div>
      </div>
    </>
  );
}

// One tap per state; moves the API refuses are greyed out.
function StatusSwitch({ item, brand, history, saving, readOnly, onMove }: {
  item: OnboardingDetailItem;
  brand: string | null;
  history: OnboardingHistoryEntry[];
  saving: boolean;
  readOnly: boolean;
  onMove: (state: ItemState) => void;
}) {
  const overdue = item.overdue;
  const forBrand = brand ? ` for ${brand}` : "";
  return (
    <div className="flex flex-col gap-1.5">
      {brand && <span className="text-sm text-gray-700 dark:text-gray-300">{brand}</span>}
      <div role="group" aria-label={`Status${forBrand}`} className="flex overflow-hidden rounded-lg border border-gray-300 dark:border-gray-700">
        {toSegments(item).map((seg) => {
          const pressed = seg.state === item.state;
          const enabled = !saving && item.moves.includes(seg.state);
          return (
            <button
              key={seg.state} type="button" aria-pressed={pressed} disabled={!pressed && !enabled}
              onClick={pressed ? undefined : () => onMove(seg.state)}
              className={`h-[34px] min-w-0 flex-1 border-l border-gray-300 px-1 text-[13px] font-medium first:border-l-0 dark:border-gray-700 ${seg.state === "not_needed" ? "flex-[1.3]" : ""} ${
                pressed
                  ? `${seg.state === "waiting" && overdue ? "bg-error-600 text-white" : PRESSED_CLASSES[seg.state]} cursor-default`
                  : enabled ? "cursor-pointer bg-white text-gray-700 hover:bg-gray-50 dark:bg-transparent dark:text-gray-300" : "bg-gray-50 text-gray-400 dark:bg-white/5 dark:text-gray-600"
              }`}
            >
              {seg.label}
            </button>
          );
        })}
      </div>
      {item.state === "waiting" && (
        <p className={`text-[13px] ${overdue ? "font-medium text-error-600" : "text-gray-500"}`}>
          {toWaitingLine(item, history)}
          {/* Chasing again restarts the wait, so the line offers it once the step has waited a day; the drawer keeps every chase. */}
          {!readOnly && (item.daysWaiting ?? 0) > 0 && (
            <>
              {" "}<span className="text-gray-300">·</span>{" "}
              <button
                type="button" disabled={saving} aria-label={`Chased today${forBrand}`} onClick={() => onMove("waiting")}
                className="cursor-pointer font-medium text-brand-500 hover:text-brand-600 disabled:cursor-default disabled:text-gray-400"
              >
                Chased today
              </button>
            </>
          )}
        </p>
      )}
    </div>
  );
}

// "Asked Oct 27 · 3 days", or once chased "Asked Oct 27 · chased Oct 30 · next from Nov 4". A chase restarts
// the wait, so the API's waitingSince is the last contact; the ask that began the wait comes from the history.
function toWaitingLine(item: OnboardingDetailItem, history: OnboardingHistoryEntry[]): string {
  const verb = item.party === "network" && item.network ? `Sent to ${ITEM_NETWORK_LABELS[item.network]}` : "Asked";
  const asked = history.find((h) => h.itemId === item.itemId && (h.action === "asked" || h.action === "sent"));
  const chased = history.find((h) => h.itemId === item.itemId && h.action === "chased");
  const lastContact = dayjs.utc(item.waitingSince ?? "");
  if (!asked || !chased || !dayjs(chased.createdAt).isAfter(asked.createdAt)) {
    const days = item.daysWaiting ?? 0;
    return `${verb} ${lastContact.format("MMM D")} · ${days === 0 ? "today" : days === 1 ? "1 day" : `${days} days`}`;
  }
  const next = item.chaseDays === null ? "" : ` · next from ${lastContact.add(item.chaseDays, "day").format("MMM D")}`;
  return `${verb} ${dayjs.utc(asked.createdAt).format("MMM D")} · chased ${lastContact.format("MMM D")}${next}`;
}

// Follows the saved notes while nothing is being typed; text typed during a save stays put.
function NotesField({ label, notes, saving, onSave }: { label: string; notes: string; saving: boolean; onSave: (notes: string) => void }) {
  const [value, setValue] = useState(notes);
  const [synced, setSynced] = useState(notes);
  if (notes !== synced) {
    setSynced(notes);
    if (value === synced) setValue(notes);
  }
  return (
    <div role="group" aria-label={label} className="flex flex-col gap-1.5">
      <TextField
        label={label} value={value} onChange={(e) => setValue(e.target.value)} multiline minRows={2} size="small" fullWidth
        placeholder="What was sent, what's missing, who you spoke to"
      />
      <div className="flex justify-end">
        <Button size="small" variant="outlined" disabled={saving || value === notes} onClick={() => onSave(value)}>Save</Button>
      </div>
    </div>
  );
}
